import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import Logo from '../components/Logo';
import { toast } from 'sonner';

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ next: false, conf: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Missing or invalid reset link. Please request a new one.');
  }, [token]);

  // Same heuristic as the change-password dialog
  const strength = (() => {
    if (!next) return { score: 0, label: '', color: '#444' };
    let s = 0;
    if (next.length >= 8) s++;
    if (next.length >= 12) s++;
    if (/[A-Z]/.test(next) && /[a-z]/.test(next)) s++;
    if (/\d/.test(next)) s++;
    if (/[^A-Za-z0-9]/.test(next)) s++;
    const labels = ['Too short', 'Weak', 'Okay', 'Good', 'Strong', 'Excellent'];
    const colors = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#06b6d4'];
    return { score: s, label: labels[s], color: colors[s] };
  })();

  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit = !!token && next.length >= 8 && next === confirm && !busy;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { token, new_password: next });
      setDone(true);
      toast.success('Password reset successfully');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Could not reset password';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg relative flex items-center justify-center px-4">
      <div className="stars-bg" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <Link to="/" aria-label="Back to WorldVibe home" data-testid="reset-home-link" className="hover:opacity-90 transition">
            <Logo size="lg" showText animate />
          </Link>
        </div>
        <div className="glass rounded-3xl p-7 cosmic-border" data-testid="reset-password-card">
          {!done ? (
            <>
              <h1 className="font-display font-bold text-2xl flex items-center gap-2">
                <Lock size={20} className="text-fuchsia-400" /> Set a new password
              </h1>
              <p className="text-white/60 text-sm mt-1">
                Pick something you haven&apos;t used elsewhere.
              </p>

              {error && !token && (
                <div className="mt-5 flex items-start gap-2 rounded-xl p-3 bg-rose-500/10 border border-rose-400/20 text-sm text-rose-200" data-testid="rp-error-banner">
                  <AlertCircle size={16} className="mt-0.5" />
                  <div>{error}</div>
                </div>
              )}

              {token && (
                <form onSubmit={submit} className="space-y-3 mt-5">
                  <PasswordField
                    value={next} onChange={setNext}
                    show={show.next} onToggle={() => setShow(s => ({ ...s, next: !s.next }))}
                    placeholder="New password (min 8 characters)"
                    testId="rp-new"
                    autoComplete="new-password"
                  />
                  {next && (
                    <div data-testid="rp-strength">
                      <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full transition-all" style={{ width: `${(strength.score / 5) * 100}%`, background: strength.color }} />
                      </div>
                      <div className="text-[11px] mt-1" style={{ color: strength.color }}>{strength.label}</div>
                    </div>
                  )}
                  <PasswordField
                    value={confirm} onChange={setConfirm}
                    show={show.conf} onToggle={() => setShow(s => ({ ...s, conf: !s.conf }))}
                    placeholder="Confirm new password"
                    testId="rp-confirm"
                    autoComplete="new-password"
                  />
                  {mismatch && (
                    <div className="text-[11px] text-rose-400" data-testid="rp-mismatch">Passwords don&apos;t match</div>
                  )}
                  {error && token && (
                    <div className="text-sm text-rose-400" data-testid="rp-error">{error}</div>
                  )}
                  <Button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid="rp-submit"
                    className="w-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 disabled:opacity-50 font-semibold"
                  >
                    {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                    Reset password
                  </Button>
                </form>
              )}

              <div className="text-center text-xs text-white/40 mt-5">
                <Link to="/login" className="text-cyan-300 hover:text-cyan-200">Back to sign in</Link>
              </div>
            </>
          ) : (
            <div className="text-center py-3" data-testid="rp-done">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle2 size={28} className="text-emerald-300" />
              </div>
              <h1 className="font-display font-bold text-2xl mt-4">Password updated</h1>
              <p className="text-white/60 text-sm mt-2">You can now sign in with your new password.</p>
              <Button
                onClick={() => nav('/login')}
                data-testid="rp-go-login"
                className="w-full mt-6 bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 font-semibold"
              >
                Go to sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordField({ value, onChange, show, onToggle, placeholder, testId, autoComplete }) {
  return (
    <div className="relative">
      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete={autoComplete}
        className="pl-9 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80"
        aria-label={show ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}
